import { useState, useEffect } from 'react';
import { X, Sparkles } from 'lucide-react';

export interface AIProjectDetails {
  name: string;
  description: string;
  /** Optional AI-generated icon as a data URL (data:image/svg+xml;base64,...) */
  icon?: string | null;
}

export interface AIProjectDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (details: AIProjectDetails) => void;
  initialDetails: AIProjectDetails;
  isLoading?: boolean;
}

export default function AIProjectDetailsModal({
  isOpen,
  onClose,
  onApply,
  initialDetails,
  isLoading = false,
}: AIProjectDetailsModalProps) {
  const [name, setName] = useState(initialDetails.name || '');
  const [description, setDescription] = useState(initialDetails.description || '');
  const [icon, setIcon] = useState<string | null>(initialDetails.icon ?? null);
  const [applyIcon, setApplyIcon] = useState(true);

  // Update form fields when initialDetails changes
  useEffect(() => {
    setName(initialDetails.name || '');
    setDescription(initialDetails.description || '');
    setIcon(initialDetails.icon ?? null);
    setApplyIcon(!!initialDetails.icon);
  }, [initialDetails]);

  if (!isOpen) return null;

  const handleApply = () => {
    onApply({
      name,
      description,
      icon: applyIcon ? icon : null,
    });
    onClose();
  };

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-2xl">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <h3 className="font-bold text-lg">AI-Generated Project Details</h3>
          </div>
          <button
            onClick={onClose}
            className="btn btn-sm btn-circle btn-ghost"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <span className="loading loading-spinner loading-lg text-primary"></span>
            <p className="mt-4 text-base-content/70">Analyzing project files...</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-base-content/70 mb-6">
              Review and edit the AI-generated project name and description before applying.
            </p>

            <div className="space-y-4">
              <div>
                <label className="label">
                  <span className="label-text font-semibold">Project Name</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered w-full"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter project name"
                />
              </div>

              <div>
                <label className="label">
                  <span className="label-text font-semibold">Description</span>
                </label>
                <textarea
                  className="textarea textarea-bordered w-full h-32"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter project description"
                />
              </div>

              {icon && (
                <div>
                  <label className="label">
                    <span className="label-text font-semibold">AI-Generated Icon</span>
                  </label>
                  <div className="flex items-center gap-4 p-3 bg-base-200 rounded-lg">
                    <div className="w-16 h-16 rounded-lg bg-base-300 flex items-center justify-center text-base-content shrink-0">
                      <img src={icon} alt="AI-generated project icon" className="w-12 h-12" />
                    </div>
                    <label className="label cursor-pointer gap-2 justify-start">
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm checkbox-primary"
                        checked={applyIcon}
                        onChange={(e) => setApplyIcon(e.target.checked)}
                      />
                      <span className="label-text">Apply this icon to the project</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-action">
              <button
                onClick={onClose}
                className="btn btn-ghost"
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                className="btn btn-primary gap-2"
                disabled={!name.trim()}
              >
                <Sparkles className="w-4 h-4" />
                Apply Changes
              </button>
            </div>
          </>
        )}
      </div>
      <div className="modal-backdrop" onClick={onClose}></div>
    </div>
  );
}
